import { useEffect, useRef, useState } from "react";
import { MessageCircle, Send, Loader2, Lock, HandHeart, Heart, BadgeCheck, Trash2 } from "lucide-react";
import { PrayerForm } from "@/components/PrayerForm";
import api from "@/lib/api";

const sessionId = () => {
  let sid = localStorage.getItem("sw_viewer_sid");
  if (!sid) {
    sid = crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2);
    localStorage.setItem("sw_viewer_sid", sid);
  }
  return sid;
};

const fmtTime = (iso) => {
  try { return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }); } catch { return ""; }
};

function NameForm({ accent, onSave }) {
  const [v, setV] = useState("");
  return (
    <form data-testid="chat-name-form" className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (v.trim()) onSave(v.trim().slice(0, 40)); }}>
      <input data-testid="chat-name-input" value={v} onChange={(e) => setV(e.target.value)} maxLength={40} placeholder="Your name to join the chat"
        className="flex-1 min-w-0 rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm text-white placeholder:text-white/40 outline-none focus:border-white/30" />
      <button data-testid="chat-join-button" type="submit" className="rounded-lg px-3 text-sm font-semibold text-black transition-opacity hover:opacity-90" style={{ background: accent }}>Join</button>
    </form>
  );
}

const OfficialBadge = ({ accent }) => (
  <span data-testid="chat-official-badge" className="inline-flex items-center gap-0.5 ml-1 align-middle rounded px-1 py-px text-[9px] font-bold uppercase tracking-wider text-black" style={{ background: accent }}>
    <BadgeCheck className="h-2.5 w-2.5" /> Official
  </span>
);

export function LiveChat({ streamId, accent }) {
  const [chat, setChat] = useState({ open: false, banned: false, messages: [] });
  const [name, setName] = useState(() => localStorage.getItem("sw_chat_name") || "");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const [prayerOpen, setPrayerOpen] = useState(false);
  const listRef = useRef(null);
  const sid = useRef(sessionId()).current;

  const load = async () => {
    try { const r = await api.get(`/public/streams/${streamId}/chat`, { params: { session_id: sid } }); setChat(r.data); } catch { /* transient */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamId]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chat.messages.length]);

  const official = chat.official_name;
  const chatName = official || name;

  const clearAll = async () => {
    if (!window.confirm("Clear all chat messages for everyone? This can't be undone.")) return;
    try { await api.delete(`/streams/${streamId}/chat`); setChat((c) => ({ ...c, messages: [] })); } catch (e2) { setErr(e2?.response?.data?.detail || "Couldn't clear chat"); }
  };

  const saveName = (n) => { localStorage.setItem("sw_chat_name", n); setName(n); };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true); setErr("");
    try {
      const r = await api.post(`/public/streams/${streamId}/chat`, { session_id: sid, name: chatName, text });
      setChat((c) => ({ ...c, messages: [...c.messages, r.data] }));
      setText("");
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Message not sent");
    } finally { setSending(false); }
  };

  return (
    <aside data-testid="live-chat" className="flex flex-col min-h-0 bg-[#0d1017] border-t min-[600px]:border-t-0 min-[600px]:border-l border-white/10 flex-1 min-[600px]:flex-none min-[600px]:w-72 relative">
      {prayerOpen && <PrayerForm streamId={streamId} sessionId={sid} defaultName={name} accent={accent} onClose={() => setPrayerOpen(false)} />}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-white/10 text-sm font-semibold text-white">
        <MessageCircle className="h-4 w-4" style={{ color: accent }} /> Live chat
        {!chat.open && <span data-testid="chat-closed-badge" className="ml-auto text-[10px] uppercase tracking-wider text-white/50">Closed</span>}
        {official && (
          <button data-testid="chat-clear-all-button" onClick={clearAll} title="Clear all messages" className={(chat.open ? "ml-auto " : "") + "h-7 px-2 rounded-md text-[11px] font-medium text-white/60 hover:text-red-300 hover:bg-red-500/10 inline-flex items-center gap-1 transition-colors"}>
            <Trash2 className="h-3.5 w-3.5" /> Clear
          </button>
        )}
      </div>
      <div className="px-3 py-2.5 border-b border-white/10 space-y-2" data-testid="chat-pinned">
        {chat.chat_welcome && <p data-testid="chat-welcome" className="text-xs text-white/75 leading-snug">{chat.chat_welcome}</p>}
        <div className="flex gap-2">
          {chat.giving_url && (
            <a data-testid="chat-give-button" href={chat.giving_url} target="_blank" rel="noopener noreferrer" className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg py-1.5 text-xs font-semibold text-black hover:opacity-90 transition-opacity" style={{ background: accent }}>
              <HandHeart className="h-3.5 w-3.5" /> {chat.giving_label || "Give Online"}
            </a>
          )}
          <button data-testid="chat-prayer-button" onClick={() => setPrayerOpen(true)} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg py-1.5 text-xs font-semibold text-white border border-white/15 hover:bg-white/10 transition-colors">
            <Heart className="h-3.5 w-3.5" /> Prayer request
          </button>
        </div>
      </div>
      <div ref={listRef} data-testid="chat-messages" className="flex-1 min-h-0 overflow-y-auto px-3 py-2 space-y-2">
        {chat.messages.length === 0 ? (
          <p className="text-xs text-white/40 text-center pt-6">{chat.open ? "No messages yet. Say hello!" : "Chat opens when the broadcast goes live."}</p>
        ) : chat.messages.map((m) => (
          <div key={m.id} data-testid={`chat-message-${m.id}`} className="text-sm leading-snug break-words animate-in fade-in slide-in-from-bottom-1 duration-300">
            <span className="font-semibold" style={{ color: m.official ? "#fff" : accent }}>{m.name}</span>{m.official && <OfficialBadge accent={accent} />}
            <span className="text-[10px] text-white/30 ml-1.5">{fmtTime(m.created_at)}</span>
            <p className="text-white/85">{m.text}</p>
          </div>
        ))}
      </div>
      <div className="p-3 border-t border-white/10 space-y-1.5">
        {!chat.open ? (
          <p data-testid="chat-closed-note" className="text-xs text-white/50 flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Chat is closed while the broadcast is offline</p>
        ) : chat.banned && !official ? (
          <p data-testid="chat-banned-note" className="text-xs text-red-300">You can't send messages in this chat.</p>
        ) : !chatName ? (
          <NameForm accent={accent} onSave={saveName} />
        ) : (
          <form onSubmit={send} className="flex gap-2">
            <input data-testid="chat-message-input" value={text} onChange={(e) => setText(e.target.value)} maxLength={300} placeholder={official ? `Post as ${official} (Official)` : `Chat as ${name}`}
              className="flex-1 min-w-0 rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm text-white placeholder:text-white/40 outline-none focus:border-white/30" />
            <button data-testid="chat-send-button" type="submit" disabled={sending || !text.trim()} className="h-9 w-9 shrink-0 rounded-lg flex items-center justify-center text-black disabled:opacity-50 transition-opacity" style={{ background: accent }}>
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </form>
        )}
        {err && <p data-testid="chat-error" className="text-[11px] text-red-300">{err}</p>}
        {chat.open && name && !chat.banned && !official && (
          <button data-testid="chat-change-name" onClick={() => { localStorage.removeItem("sw_chat_name"); setName(""); }} className="text-[10px] text-white/40 hover:text-white/70">Change name</button>
        )}
      </div>
    </aside>
  );
}
